import React, { useState, useContext, useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import * as Sentry from '@sentry/browser';
import Media from 'react-media';
import AuthService from '../../../services/auth/AuthService';
import { Button as BaseButton } from '../../../components/buttons';
import FormController from '../../../components/globals/FormController';
import GlobalTheme from '../../../styled/GlobalTheme';
import SignUpBox from './SignUpBox';
import LoginOptionsBox from './LoginOptionsBox';
import { AlertsContext } from '../../../contexts/AlertsContext';
import FacebookButton from '../../auth/components/FacebookButton';
import PasswordInput from '../../../components/globals/PasswordInput';
import { SEVERITY } from '../../../utils/constants/alerts';
import StrokeBox from './StrokeBox';
import AppleButton from '../../auth/components/AppleButton';
import mediaQueries from '../../../utils/mediaQueries';

const Container = styled.div`
  display: flex;
  justify-content: center;
  ${GlobalTheme.breakpoints.down('sm')}{
    padding: 14px 24px 38px 24px;
  }
  @media ${mediaQueries.mdUp} {
    padding-bottom: 100px;
  }
`;

const FormContainer = styled.div`
  max-width: 392px;
  width: 100%;
`;

const Button = styled(BaseButton)`
  && {
    margin-top: 27px;
    width: 100%;
  }
`;

const ButtonContainerSN = styled.div`
  text-align: center;
  margin: 0 15%;
  margin-top: 25px;
  ${GlobalTheme.breakpoints.up('md')} {
    width: 70%;
  }
`;

const Form = ({ setOpen }) => {
  const { showAlert, showError } = useContext(AlertsContext);
  const intl = useIntl();
  const [user, setUserInfo] = useState({
    useremail: '',
    userpassword: '',
  });

  const handleInputChange = (e) => {
    const { id, value } = e.target;
    setUserInfo({
      ...user,
      [id]: value,
    });
  };

  const performLogin = async () => {
    try {
      await AuthService.login(user.useremail, user.userpassword, 'true');
    } catch (e) {
      Sentry.captureException(e);
      showError(intl.formatMessage({
        id: 'alert.error.loginEmail',
        defaultMessage: 'Your mail or password is incorrect. Please write again.',
      }));
    }
  };

  useEffect(() => {
    const existFlag = window.localStorage.getItem('registederuser');
    if (existFlag) {
      window.localStorage.removeItem('registederuser');
      showAlert(
        intl.formatMessage({
          id: 'k.login.message.userexist',
          defaultMessage: 'There is already an account with your email associated. Log in with that email.',
        }), SEVERITY.INFO,
      );
    }
  }, []);

  return (
    <Media queries={mediaQueries}>
      {(matches) => (
        <Container>
          <FormContainer>
            {matches.mdDownLess && (
              <SignUpBox />
            )}
            <FormController
              id="useremail"
              onChange={handleInputChange}
              label="E-mail"
              value={user.useremail}
            />
            <PasswordInput
              id="userpassword"
              onChange={handleInputChange}
              label="Password"
              type="password"
              value={user.userpassword}
            />
            <Button size="l" onClick={() => performLogin()}>
              <FormattedMessage id="k.login" defaultMessage="Log in" />
            </Button>
            <LoginOptionsBox setOpen={setOpen} />
            <StrokeBox />

            <ButtonContainerSN>
              <FacebookButton buttonType="login" />
            </ButtonContainerSN>

            <ButtonContainerSN>
              <AppleButton wantsFree={false} buttonType="signin" />
            </ButtonContainerSN>
          </FormContainer>
        </Container>
      )}
    </Media>
  );
};

Form.propTypes = {
  setOpen: PropTypes.func.isRequired,
};

export default Form;
